import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  GENERATE_TRUTO_LINK_TOKEN,
  GET_AVAILABLE_INTEGRATIONS,
  GET_CUSTOMER_INTEGRATIONS,
  GET_SYSTEM_OWNER_INTEGRATION_MAPPING,
  STORE_INTEGRATION_RESOURCE_DETAILS,
} from "../../../apis/settingsApis/integration-apis/integrationApis";
import { useMutation, useQuery } from "@apollo/client";
import authenticate from "@truto/truto-link-sdk";
import {
  Typography,
  Box,
  CircularProgress,
  Container,
  Grid,
  useTheme,
  Slide,
} from "@mui/material";
import SearchBar from "../../../components/SearchBar/SearchBar";
import FilterSidebar from "./FilterSidebar/FilterSidebar";
import IntegrationConnectionDrawer from "./IntegrationConnectionDrawer/IntegrationConnectionDrawer";
import IntegrationManagementDrawer from "./IntegrationManagementDrawer/IntegrationManagementDrawer";
import IntegrationCard from "./IntegrationCard/IntegrationCard";
import EmptyIntegrationsState from "./EmptyIntegrationsState/EmptyIntegrationsState";
import DomainGroupHeader from "./DomainGroupHeader/DomainGroupHeader";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import { useLocation, useNavigate } from "react-router-dom";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";
import { MasterLookupItemsType } from "../../../utils/enums";
import { pollConnectHealth } from "./utils/integrationUtils";
import Role from "../../../utils/roles";

export default () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("All");
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const handleShowSnackbar = (info) => {
    setSnackbarInfo({
      ...info,
      Transition: TransitionComponent,
    });
  };

  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [customerIntegrations, setCustomerIntegrations] = useState(undefined);

  // Drawer state for cloud integrations and IDP
  const [connectionDrawerOpen, setConnectionDrawerOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  // Management drawer state
  const [managementDrawerOpen, setManagementDrawerOpen] = useState(false);
  const [managementIntegration, setManagementIntegration] = useState(null);
  const {
    data: customerIntegrationsResponse,
    loading: loadingCustomerIntegrations,
    error,
    refetch: refetchCustomerIntegrations,
  } = useQuery(GET_CUSTOMER_INTEGRATIONS);

  const {
    data: systemOwnerMappingResponse,
    refetch: refetchSystemOwnerMapping,
  } = useQuery(GET_SYSTEM_OWNER_INTEGRATION_MAPPING, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const [generateTrutoLinkToken, { loading }] = useMutation(
    GENERATE_TRUTO_LINK_TOKEN
  );

  const [storeIntegrationDetails] = useMutation(
    STORE_INTEGRATION_RESOURCE_DETAILS
  );

  const {
    loading: loadingAvailableIntegrations,
    data: availableIntegrationsResponse,
    refetch: refetchAvailableIntegrations,
  } = useQuery(GET_AVAILABLE_INTEGRATIONS);

  // Parse URL query parameters and set initial domain filter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const domainParam = searchParams.get("domain");

    if (domainParam) {
      // Set the domain from URL parameter
      setSelectedDomain(domainParam);
    }
  }, [location.search]);

  // Handle domain selection and update URL
  const handleDomainSelect = useCallback(
    (domain) => {
      setSelectedDomain(domain);

      // Update URL with domain parameter
      const searchParams = new URLSearchParams(location.search);
      if (domain && domain !== "All") {
        searchParams.set("domain", domain);
      } else {
        searchParams.delete("domain");
      }

      // Update URL without page reload
      const newSearch = searchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (
      !loadingCustomerIntegrations &&
      customerIntegrationsResponse?.getAccountDetails?.userIntegrationsDetails
    ) {
      setCustomerIntegrations(
        customerIntegrationsResponse?.getAccountDetails?.userIntegrationsDetails
          ?.list ?? []
      );
    }
  }, [customerIntegrationsResponse, loadingCustomerIntegrations]);

  useEffect(() => {
    if (
      !loadingAvailableIntegrations &&
      availableIntegrationsResponse?.getAvailableIntegrations?.list &&
      customerIntegrations != undefined
    ) {
      const alreadyIntegratedKeys =
        customerIntegrations?.map((integration) => {
          return integration.integration_key;
        }) ?? [];
      setAvailableIntegrations(
        availableIntegrationsResponse?.getAvailableIntegrations?.list
          .filter((val) => !alreadyIntegratedKeys?.includes(val.key))
          ?.map((val) => val.key)
      );
    }
  }, [
    availableIntegrationsResponse,
    loadingAvailableIntegrations,
    customerIntegrations,
  ]);

  const userDetails =
    customerIntegrationsResponse?.getAccountDetails?.userDetails || {};
  const isAdmin = (userDetails?.roles || []).some(
    (r) => String(r?.role_description || "").toLowerCase() === Role.ADMIN
  );
  const currentEmployeeDataId = userDetails?.employee_data_id;

  const ownersByResourceId = useMemo(() => {
    const rows =
      systemOwnerMappingResponse?.getSystemOwnerAndIntegrationMapping?.data ||
      [];
    const map = {};
    for (const row of rows) {
      const key = row.integrated_resource_id;
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(row);
    }
    return map;
  }, [systemOwnerMappingResponse]);

  const hasHrmsConnected = useMemo(() => {
    const enabled = customerIntegrations || [];
    const available =
      availableIntegrationsResponse?.getAvailableIntegrations?.list || [];

    const hrmsIntegrations = available
      .filter(
        (int) =>
          int.domains &&
          int.domains.length > 0 &&
          int.domains[0].key === MasterLookupItemsType.HRMS_PROVIDER
      )
      .map((int) => int.key);

    return enabled.some(
      (int) =>
        hrmsIntegrations.includes(int.integration_key) && int.is_active === true
    );
  }, [customerIntegrations, availableIntegrationsResponse]);

  const hasIdpConnected = useMemo(() => {
    const enabled = customerIntegrations || [];
    const available =
      availableIntegrationsResponse?.getAvailableIntegrations?.list || [];

    const idpIntegrations = available
      .filter(
        (int) =>
          int.domains &&
          int.domains.length > 0 &&
          int.domains[0].key === MasterLookupItemsType.IDP_PROVIDER
      )
      .map((int) => int.key);

    return enabled.some(
      (int) =>
        idpIntegrations.includes(int.integration_key) && int.is_active === true
    );
  }, [customerIntegrations, availableIntegrationsResponse]);

  // Count of distinct IDP integrations (native idp_provider OR use_as_idp).
  // Used to enforce: you can't disable "use as IDP" on your only IDP.
  const idpIntegrationCount = useMemo(() => {
    const enabled = customerIntegrations || [];
    const available =
      availableIntegrationsResponse?.getAvailableIntegrations?.list || [];
    const nativeIdpKeys = new Set(
      available
        .filter(
          (int) =>
            int.domains?.length > 0 &&
            int.domains[0].key === MasterLookupItemsType.IDP_PROVIDER
        )
        .map((int) => int.key)
    );
    const idpKeys = new Set();
    enabled.forEach((acc) => {
      if (acc.is_active !== true) return;
      if (nativeIdpKeys.has(acc.integration_key) || acc.use_as_idp === true) {
        idpKeys.add(acc.integration_key);
      }
    });
    return idpKeys.size;
  }, [customerIntegrations, availableIntegrationsResponse]);

  // Get all integrations with connection status (only those with domains)
  const allIntegrations = useMemo(() => {
    const enabled = customerIntegrations || [];
    const available =
      availableIntegrationsResponse?.getAvailableIntegrations?.list || [];

    // Create a Set of integration keys that have at least one ACTIVE account
    const activeIntegrationKeys = new Set(
      enabled
        .filter((int) => int.is_active === true)
        .map((int) => int.integration_key)
    );

    // Map all available integrations with their connection status
    // Filter out integrations without domains
    return available
      .filter((int) => int.domains && int.domains.length > 0)
      .map((int) => ({
        key: int.key,
        label: int.label,
        category: int.category,
        logo_path: int.logo_path,
        idpEligible: int.idp_eligible === true,
        domains: int.domains,
        domainTitle: int.domains[0].title,
        domainKey: int.domains[0].key,
        // Include all connected accounts data (both active and inactive)
        connectedAccounts: enabled.filter(
          (acc) => acc.integration_key === int.key
        ),
        activeConnectedAccounts: enabled.filter(
          (acc) => acc.integration_key === int.key && acc.is_active === true
        ),
        inactiveConnectedAccounts: enabled.filter(
          (acc) => acc.integration_key === int.key && acc.is_active === false
        ),
        // available_integrations.id for this integration (from any connected
        // account row) — needed to toggle the "use as IDP" override.
        integrationId:
          enabled.find((acc) => acc.integration_key === int.key)
            ?.integration_id ?? null,
        // True if any active account of this integration is flagged as IDP.
        useAsIdp: enabled.some(
          (acc) =>
            acc.integration_key === int.key &&
            acc.is_active === true &&
            acc.use_as_idp === true
        ),
        // Current IdP attribution priority (from any connected account row).
        idpPriority:
          enabled.find(
            (acc) =>
              acc.integration_key === int.key &&
              acc.idp_priority !== null &&
              acc.idp_priority !== undefined
          )?.idp_priority ?? null,
      }));
  }, [customerIntegrations, availableIntegrationsResponse]);

  // Get domains with counts (using domain keys)
  const domainsWithCounts = useMemo(() => {
    const domainMap = {};
    allIntegrations.forEach((integration) => {
      const domainKey = integration.domainKey;
      const domainTitle = integration.domainTitle;

      if (!domainMap[domainKey]) {
        domainMap[domainKey] = { title: domainTitle, count: 0 };
      }
      domainMap[domainKey].count += 1;
    });

    // Sort domains alphabetically by title
    const sortedDomainKeys = Object.keys(domainMap).sort((a, b) =>
      domainMap[a].title.localeCompare(domainMap[b].title)
    );

    return sortedDomainKeys.map((key) => ({
      key: key,
      title: domainMap[key].title,
      count: domainMap[key].count,
    }));
  }, [allIntegrations]);

  // Filter integrations based on search and domain
  const filteredIntegrations = useMemo(() => {
    let filtered = allIntegrations;

    // Filter by domain key only
    if (selectedDomain !== "All") {
      filtered = filtered.filter((int) => int.domainKey === selectedDomain);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (int) =>
          int.label?.toLowerCase().includes(query) ||
          int.domainTitle?.toLowerCase().includes(query) ||
          int.category?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allIntegrations, selectedDomain, searchQuery]);

  // Group integrations by domain key and sort alphabetically
  const groupedIntegrations = useMemo(() => {
    const groups = {};

    filteredIntegrations.forEach((integration) => {
      const domainKey = integration.domainKey;
      const domainTitle = integration.domainTitle;

      if (!groups[domainKey]) {
        groups[domainKey] = {
          key: domainKey,
          title: domainTitle,
          integrations: [],
        };
      }
      groups[domainKey].integrations.push(integration);
    });

    // Sort domain groups by title alphabetically
    const sortedDomainKeys = Object.keys(groups).sort((a, b) =>
      groups[a].title.localeCompare(groups[b].title)
    );

    return sortedDomainKeys.map((key) => ({
      key: key,
      domain: groups[key].title,
      integrations: groups[key].integrations.sort((a, b) =>
        (a.label || "").localeCompare(b.label || "")
      ),
    }));
  }, [filteredIntegrations]);

  const connectViaTruto = async (integration) => {
    const integrationKey =
      typeof integration === "string" ? integration : integration?.key;

    try {
      const { data } = await generateTrutoLinkToken({
        variables: {},
      });

      if (!loading) {
        const linkToken = data.generateTrutoLinkToken.linkToken;

        if (!linkToken) {
          handleShowSnackbar({
            message: "Failed to generate link token. Please try again.",
            vertical: "top",
            horizontal: "right",
          });
          return;
        }

        if (!integrationKey && availableIntegrations.length === 0) {
          handleShowSnackbar({
            message: "Something went wrong, No integrations available",
            vertical: "top",
            horizontal: "right",
          });
          return;
        }

        // Invoke Truto Link SDK with specific integration if provided
        const response = await authenticate(linkToken, {
          noBack: true,
          skipRapidForm: false,
          iframe: true,
          integrations: integrationKey
            ? [integrationKey]
            : availableIntegrations,
        });

        if (response.result == "success") {
          const integratedAccountId = response.integrated_account_id;
          const integrationKey = response.integration;
          storeIntegrationDetails({
            variables: {
              integratedAccountId,
              integrationKey,
              integrationType: "TRUTO",
              sync: true,
            },
          })
            .then(async (result) => {
              if (result?.data?.storeIntegrationResourceId?.success) {
                const health = await pollConnectHealth(
                  refetchCustomerIntegrations,
                  integratedAccountId
                );
                if (health.status === "failed") {
                  handleShowSnackbar({
                    message: health.inactivityReason
                      ? `Connection failed: ${health.inactivityReason}`
                      : "Connection failed. Please reconnect.",
                    vertical: "top",
                    horizontal: "right",
                  });
                } else {
                  handleShowSnackbar({
                    message: "Integration connected, initial data sync is in progress.",
                    vertical: "top",
                    horizontal: "right",
                  });
                }
              } else {
                handleShowSnackbar({
                  message: result?.data?.storeIntegrationResourceId?.message || "Failed to connect integration. Please try again.",
                  vertical: "top",
                  horizontal: "right",
                });
              }
              refetchAvailableIntegrations();
            })
            .catch((err) => {
              console.log("error occurred while storing integration details");
            });
        } else {
          console.error("Error during Link connection:", error);
        }
        console.log(`Successfully connected to ${response}`);
      }
    } catch (error) {
      console.error("Error during Link connection:", error);
    }
  };

  const handleConnectIntegration = async (integration) => {
    const integrationKey =
      typeof integration === "string" ? integration : integration?.key;
    const domainKey =
      typeof integration === "object" ? integration?.domainKey : null;

    if (domainKey === MasterLookupItemsType.IDP_PROVIDER) {
      setSelectedIntegration(integration);
      setConnectionDrawerOpen(true);
      return;
    }

    // Check if this is a cloud provider integration (AWS, Azure, GCP)
    const cloudProviders = ["aws", "azure", "gcp"];
    if (cloudProviders.includes(integrationKey)) {
      // Open connection drawer for cloud providers
      setSelectedIntegration({ key: integrationKey });
      setConnectionDrawerOpen(true);
      return;
    }
    if (integrationKey === "rego_watch_tower") {
      setSelectedIntegration(integration);
      setConnectionDrawerOpen(true);
      return;
    }

    await connectViaTruto(integration);
  };

  const handleConnectionDrawerClose = () => {
    setConnectionDrawerOpen(false);
    setSelectedIntegration(null);
  };

  const handleManageIntegration = (integration) => {
    setManagementIntegration(integration);
    setManagementDrawerOpen(true);
  };

  const handleManagementDrawerClose = () => {
    setManagementDrawerOpen(false);
    setManagementIntegration(null);
  };

  useEffect(() => {
    if (managementDrawerOpen && managementIntegration) {
      refetchSystemOwnerMapping();
    }
  }, [managementDrawerOpen, managementIntegration, refetchSystemOwnerMapping]);

  const handleConnectNewFromManagement = (integration) => {
    // Close management drawer and open connection drawer
    setManagementDrawerOpen(false);
    handleConnectIntegration(integration);
  };

  const handleDeleteSuccess = async () => {
    // Refetch integrations after deletion
    await refetchCustomerIntegrations();
    await refetchAvailableIntegrations();
    await refetchSystemOwnerMapping();
  };

  // Handler for storing integration details (used by AWS, Azure, GCP drawers)
  const handleStoreIntegrationDetails = async (variables) => {
    try {
      const result = await storeIntegrationDetails({
        variables,
      });

      if (!result || !result.data || result.error) {
        handleShowSnackbar({
          message:
            "An error has occurred. Please contact your service provider.",
          vertical: "top",
          horizontal: "right",
        });
        return { success: false, error: true };
      }

      // Check if the mutation was successful
      const success =
        result?.data?.storeIntegrationResourceId?.success !== false;

      if (success) {
        handleShowSnackbar({
          message: "Integration connected, initial data sync is in progress.",
          vertical: "top",
          horizontal: "right",
        });
      } else {
        handleShowSnackbar({
          message:
            result?.data?.storeIntegrationResourceId?.message ||
            "Failed to connect integration. Please try again.",
          vertical: "top",
          horizontal: "right",
        });
      }

      // Refetch integrations
      await refetchCustomerIntegrations();
      await refetchAvailableIntegrations();

      return { success, data: result.data };
    } catch (error) {
      console.error("Error storing integration details:", error);
      handleShowSnackbar({
        message: "An error has occurred. Please contact your service provider.",
        vertical: "top",
        horizontal: "right",
      });
      return { success: false, error: true };
    }
  };

  // Update managementIntegration when customerIntegrations changes
  useEffect(() => {
    if (
      managementIntegration &&
      customerIntegrations !== undefined &&
      allIntegrations.length > 0
    ) {
      const updatedIntegration = allIntegrations.find(
        (int) => int.key === managementIntegration.key
      );
      if (updatedIntegration) {
        setManagementIntegration(updatedIntegration);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerIntegrations]);

  if (loadingCustomerIntegrations || loadingAvailableIntegrations) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Information Steps Card */}
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps you connect and manage integrations with third-party tools and services to automate compliance monitoring."
          steps={[
            "Browse **available integrations** organized by domain (Infrastructure, People, Risks). Each integration helps automate evidence gathering and monitoring.",

            'Click **"Connect"** on any integration to set it up. This will open a configuration dialog where you can provide credentials or authorize the connection.',

            "View **connected accounts** for each integration. Green checkmarks indicate active connections, while gray icons show inactive or disconnected accounts.",

            "Use **domain filters** in the sidebar to focus on specific types of integrations (Infrastructure, People, Risks).",

            "Use the **search bar** to quickly find integrations by name, domain, or category.",

            "Manage existing connections by clicking on connected accounts to view details, update credentials, or disconnect integrations.",

            "For each connected account, **data owners** (internal users) can designate who may manage owner access for that connection. At least one owner must remain.",
          ]}
          footerText="💡 Connect integrations to automatically gather evidence and monitor your systems, reducing manual compliance work."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* Fixed Header Section */}
      <Box
        sx={{
          flexShrink: 0,
          pb: 3,
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            fontWeight="600"
            color={theme.customColors.text.strong}
            sx={{ mb: 0.5 }}
          >
            All Integrations
          </Typography>
          <Typography
            variant="body2"
            color={theme.customColors.text.secondary}
          >
            Connect your tools and update existing integrations to activate
            automated evidence gathering and monitoring.
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box>
          <SearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search integrations..."
            count={filteredIntegrations.length}
            countLabel="integrations found"
          />
        </Box>
      </Box>

      {/* Main Content Area - Scrollable Container */}
      <Box sx={{ flex: 1, overflow: "hidden", pt: 3 }}>
        <Grid container spacing={3} sx={{ height: "100%" }}>
          {/* Left Sidebar - Domains (Fixed with internal scroll) */}
          <Grid
            item
            xs={12}
            md={2.5}
            sx={{
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.customColors.border.main,
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: theme.customColors.border.strong,
                },
              }}
            >
              <FilterSidebar
                categories={domainsWithCounts}
                selectedCategory={selectedDomain}
                onCategorySelect={handleDomainSelect}
                totalCount={allIntegrations.length}
              />
            </Box>
          </Grid>

          {/* Right Content - Integration Cards (Scrollable with hidden scrollbar) */}
          <Grid
            item
            xs={12}
            md={9.5}
            sx={{
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.customColors.border.main,
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: theme.customColors.border.strong,
                },
              }}
            >
              {groupedIntegrations.length === 0 ? (
                <EmptyIntegrationsState />
              ) : (
                groupedIntegrations.map(({ domain, integrations }) => (
                  <Box key={domain} sx={{ mb: 4 }}>
                    {/* Domain Title */}
                    <DomainGroupHeader
                      domain={domain}
                      showDomain={groupedIntegrations.length > 1}
                    />

                    {/* Integration Cards */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2.5,
                      }}
                    >
                      {integrations.map((integration) => (
                        <IntegrationCard
                          key={integration.key}
                          integration={integration}
                          hasHrmsConnected={hasHrmsConnected}
                          hasIdpConnected={hasIdpConnected}
                          onManageIntegration={handleManageIntegration}
                          onConnectIntegration={handleConnectIntegration}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Connection Drawer for AWS, Azure, GCP, and IDP */}
      {selectedIntegration && (
        <IntegrationConnectionDrawer
          open={connectionDrawerOpen}
          onClose={handleConnectionDrawerClose}
          integrationKey={selectedIntegration.key}
          integrationLabel={selectedIntegration.label}
          integrationLogo={selectedIntegration.logo_path}
          domainKey={selectedIntegration.domainKey}
          refetchIntegrationDetails={refetchCustomerIntegrations}
          onStoreIntegrationDetails={handleStoreIntegrationDetails}
          onShowSnackbar={handleShowSnackbar}
        />
      )}

      {/* Management Drawer for managing multiple connections */}
      <IntegrationManagementDrawer
        open={managementDrawerOpen}
        onClose={handleManagementDrawerClose}
        integration={managementIntegration}
        onConnectNew={handleConnectNewFromManagement}
        onDeleteSuccess={handleDeleteSuccess}
        onShowSnackbar={handleShowSnackbar}
        ownersByResourceId={ownersByResourceId}
        currentEmployeeDataId={currentEmployeeDataId}
        isAdmin={isAdmin}
        onRefetchSystemOwners={refetchSystemOwnerMapping}
        onRefetchIntegrations={refetchCustomerIntegrations}
        idpIntegrationCount={idpIntegrationCount}
      />

      {/* Snackbar for notifications */}
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};
